import { useCallback, useEffect, useState } from 'react';
import { buscarClientes, crearTercero, type ClienteBusqueda } from '../../lib/api-catalogo';
import { Boton, Campo } from '../../ui/base';
import { FormularioTercero, type DatosFormularioTercero } from '../../ui/FormularioTercero';
import { Modal } from '../../ui/Modal';

interface Props {
  negocioId: string;
  abierto: boolean;
  onCerrar: () => void;
  onElegir: (cliente: { id: string; nombre: string; numeroDocumento: string } | null) => void;
}

export function SelectorCliente({ negocioId, abierto, onCerrar, onElegir }: Props) {
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<ClienteBusqueda[]>([]);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!abierto || creando) return;
    let vivo = true;
    const t = setTimeout(() => {
      buscarClientes(negocioId, texto)
        .then((r) => vivo && setResultados(r))
        .catch(() => vivo && setResultados([]));
    }, 200);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [abierto, creando, texto, negocioId]);

  const reiniciar = useCallback(() => {
    setTexto('');
    setResultados([]);
    setCreando(false);
    setError('');
  }, []);

  const cerrar = useCallback(() => {
    reiniciar();
    onCerrar();
  }, [reiniciar, onCerrar]);

  const elegir = useCallback(
    (cliente: { id: string; nombre: string; numeroDocumento: string } | null) => {
      reiniciar();
      onElegir(cliente);
    },
    [reiniciar, onElegir]
  );

  async function crear(datos: DatosFormularioTercero) {
    setError('');
    setOcupado(true);
    try {
      await crearTercero(negocioId, { ...datos, esCliente: true, esProveedor: false });
      const encontrados = await buscarClientes(negocioId, datos.numeroDocumento.trim());
      const nuevo = encontrados[0];
      if (nuevo) elegir({ id: nuevo.id, nombre: nuevo.nombre, numeroDocumento: nuevo.numeroDocumento });
      else cerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el cliente.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal abierto={abierto} titulo="Cliente del comprobante" onCerrar={cerrar}>
      {!creando ? (
        <div className="space-y-3">
          <Campo
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar por nombre o documento"
            autoFocus
          />
          <button
            onClick={() => elegir(null)}
            className="rounded-control hover:bg-primary/10 w-full px-3 py-2 text-left font-semibold min-h-(--alto-control)"
            data-e2e="cliente-consumidor-final"
          >
            Consumidor final
          </button>
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {resultados.map((c) => (
              <button
                key={c.id}
                onClick={() => elegir({ id: c.id, nombre: c.nombre, numeroDocumento: c.numeroDocumento })}
                className="rounded-control hover:bg-primary/10 flex w-full items-center justify-between px-3 py-2 text-left min-h-(--alto-control)"
              >
                <span>{c.nombre}</span>
                <span className="num text-muted text-contenido">{c.numeroDocumento}</span>
              </button>
            ))}
            {texto.trim() && resultados.length === 0 && (
              <p className="text-muted px-3 py-2 text-contenido">Sin clientes con «{texto.trim()}».</p>
            )}
          </div>
          <Boton variante="contorno" onClick={() => setCreando(true)} data-e2e="cliente-nuevo">
            Registrar un cliente nuevo
          </Boton>
        </div>
      ) : (
        <FormularioTercero
          contexto="cliente"
          ocupado={ocupado}
          errorServidor={error}
          onGuardar={(d) => void crear(d)}
          onCancelar={() => setCreando(false)}
        />
      )}
    </Modal>
  );
}
